import React from 'react';
import { Modal, notification } from 'antd';
import dayjs from 'dayjs';
import { DATE_TIME_AM_PM_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import STATUS from 'constants/status';
import { patchRequest } from 'api/apiClient';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { fetchVisits } from 'store/slices/visitsSlice';
import { searchVisitors, searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { useTranslation } from 'react-i18next';

interface ProfileEditModalProps {
    open: boolean;
    onClose: () => void;
    setDrawerLoading?: (loading: boolean) => void;
}

const ProfileEditModal: React.FC<ProfileEditModalProps> = ({ open, onClose, setDrawerLoading }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { selectedVisit, selectedVisitor } = useSelector((state: RootState) => state.visits);
    const { invitationConfig } = useSelector((state: RootState) => state.visitorInvitation);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const lastSearchParams = useSelector((state: RootState) => state.visitorSearch.lastSearchParams);

    const [form] = OpForm.useForm();
    const { t } = useTranslation();
    const initialValues = {
        ...selectedVisitor,
        signIn: selectedVisitor?.signIn ? dayjs(selectedVisitor.signIn) : null,
        signOut: selectedVisitor?.signOut ? dayjs(selectedVisitor.signOut) : null,
    }

    const editVisitor = async (updatedVisitor: any) => {
        setDrawerLoading?.(true);
        try {
            const payload = {
                ...(updatedVisitor.firstName != null && { firstName: (updatedVisitor.firstName).trim() }),
                ...(updatedVisitor.middleName != null && { middleName: (updatedVisitor.middleName).trim() }),
                ...(updatedVisitor.lastName != null && { lastName: (updatedVisitor.lastName).trim() }),
                ...(updatedVisitor.email != null && { email: updatedVisitor.email }),
                ...(updatedVisitor.mobilePhone != null && { mobilePhone: updatedVisitor.mobilePhone }),
                ...(updatedVisitor.signIn != null && { signIn: dayjs(updatedVisitor.signIn).format(DATE_TIME_FORMAT) }),
                ...(updatedVisitor.signOut != null && { signOut: dayjs(updatedVisitor.signOut).format(DATE_TIME_FORMAT) }),
            };

            if (payload.signOut && payload.signIn && dayjs(payload.signOut).isBefore(dayjs(payload.signIn))) {
                notification.error({
                    message: t('notification.error'),
                    description: t('visitors.profile.notification.invalidDates'),
                    placement: 'bottomRight',
                });
                return;
            }

            await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitors/${selectedVisitor?.id}`, payload);
            await dispatch(fetchVisits({ orgId }));
            await dispatch(searchVisitorsToday({ orgId }));
            if (Object.keys(lastSearchParams).length !== 0) {
                await dispatch(searchVisitors({
                    ...lastSearchParams,
                    orgId,
                    siteId: lastSearchParams.siteId || globalLocationId! // 사이트 우선순위 적용
                }));
            }

            notification.success({
                message: t('notification.success'),
                description: t('visitors.profile.notification.updateSuccess'),
                placement: 'bottomRight',
            });
        } catch (error) {
            console.error('Failed to update visitor:', error);
            notification.error({
                message: t('notification.error'),
                description: t('visitors.profile.notification.updateError'),
                placement: 'bottomRight',
            });
        } finally {
            setDrawerLoading?.(false);
        }
    };

    return (
        <Modal
            open={open}
            title={t('visitors.profile.edit.title')}
            okText={t('visitors.profile.edit.save')}
            cancelText={t('visitors.profile.edit.cancel')}
            onCancel={() => {
                form.resetFields();
                onClose();
            }}
            onOk={async () => {
                try {
                    await form.validateFields();
                    form.submit();
                    onClose();
                } catch (errorInfo) {
                    // Validation failed - error messages will be shown under form fields
                    // Do not close modal
                }
            }}
            centered
        >
            <OpForm
                form={form}
                initialValues={initialValues}
                onSubmit={({ values }) => {
                    editVisitor(values);
                    form.resetFields();
                }}
                defaultButtons={false}
                hasError={false}
            >
                {(selectedVisitor?.status === STATUS.PENDING.id) && (
                    <>
                        <OpForm.Input
                            label={t('visitors.profile.edit.form.firstName')}
                            name="firstName"
                            rules={[{ required: true, message: t('visitors.profile.edit.form.firstNameRequired') }]}
                        />
                        <OpForm.Input
                            label={t('visitors.profile.edit.form.lastName')}
                            name="lastName"
                            rules={[{ required: true, message: t('visitors.profile.edit.form.lastNameRequired') }]}
                        />
                        <OpForm.Input
                            label={t('visitors.profile.edit.form.middleName')}
                            name="middleName"
                        />
                        <OpForm.Input
                            label={t('visitors.profile.edit.form.email')}
                            name="email"
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                                {
                                    required: (selectedVisit?.visitStatus.id === STATUS.PENDING.id && invitationConfig?.email.required === 1),
                                    message: t('visitors.profile.edit.form.emailRequired')
                                },
                                {
                                    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                    message: t('visitors.visitorList.emailInvalid')
                                }
                            ]}
                        />
                        <OpForm.Input
                            label={t('visitors.profile.edit.form.mobilePhone')}
                            name="mobilePhone"
                            rules={[
                                {
                                    required: (selectedVisit?.visitStatus.id === STATUS.PENDING.id && invitationConfig?.visitPhone.required === 1),
                                    message: t('visitors.profile.edit.form.mobilePhoneRequired')
                                }
                            ]}
                        />
                    </>
                )}
                {(selectedVisitor?.status === STATUS.SIGNED_IN.id) && ( // Sign In time field is required when status is 'Signed In'
                    <OpForm.DatePicker
                        format={DATE_TIME_AM_PM_FORMAT}
                        name="signIn"
                        label={t('visitors.profile.edit.form.signIn')}
                        minuteStep={5}
                        showTime
                        rules={[
                            { required: true, message: t('visitors.header.editModal.form.dateRequired') },
                            {
                                validator: (_, value) =>
                                    value && value.isAfter(dayjs()) // It cannot be in the future time
                                        ? Promise.reject(new Error(t('visitors.header.editModal.notification.invalidSignInTimeError')))
                                        : Promise.resolve(),
                            },
                        ]}
                    />
                )}

                {(selectedVisitor?.status === STATUS.SIGNED_OUT.id) && (
                    <>
                        <OpForm.DatePicker
                            format={DATE_TIME_AM_PM_FORMAT}
                            name="signIn"
                            label={t('visitors.profile.edit.form.signIn')}
                            minuteStep={5}
                            showTime
                            rules={[
                                { required: true, message: t('visitors.profile.edit.form.dateRequired') },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const signOut = getFieldValue('signOut');

                                        if (value && value.isAfter(dayjs())) { // it cannot be in the future time
                                            return Promise.reject(new Error(t('visitors.header.editModal.notification.invalidSignInTimeError')))
                                        }

                                        if (value && signOut && value.isSameOrAfter(signOut)) { // it cannot be after end date
                                            return Promise.reject(new Error(t('visitors.header.editModal.notification.startDateAfterEndDateError')));
                                        }
                                        return Promise.resolve();
                                    },

                                })
                            ]}
                            onChange={() => form.validateFields(['signOut'])}
                        />
                        <OpForm.DatePicker
                            format={DATE_TIME_AM_PM_FORMAT}
                            name="signOut"
                            label={t('visitors.profile.edit.form.signOut')}
                            minuteStep={5}
                            showTime
                            rules={[
                                { required: true, message: t('visitors.profile.edit.form.dateRequired') },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const signIn = getFieldValue('signIn');
                                        
                                        if (value && signIn && value.isSameOrBefore(signIn)) {
                                            return Promise.reject(new Error(t('visitors.header.editModal.notification.invalidDateRangeError')));
                                        }
                                        return Promise.resolve();
                                    },

                                })
                            ]}
                            onChange={() => form.validateFields(['signIn'])}
                        />
                    </>
                )}
            </OpForm>

        </Modal>
    );
};

export default ProfileEditModal;
